import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Box, Flex, Text, Icon, VStack, Image } from "@chakra-ui/react";
import { FaPlay, FaPause, FaRedo, FaChevronRight } from "react-icons/fa";

const demoSteps = [
  {
    id: "intro",
    title: "Transforma EditLaTeX",
    text: "A modern, easy-to-use cloud platform changing how publishers, editors, and authors work together to build flawless publications.",
    image: "/images/white-logo.png",
    isHero: true,
    duration: 8000
  },
  {
    id: "signup",
    title: "Quick & Easy Access",
    features: [
      { bold: "Cloud-Based Platform", desc: "No complex LaTeX software installations required.", image: "/images/video/CloudBased.png" },
      { bold: "Instant Sign Up", desc: "Get straight to your dashboard in seconds.", image: "/images/video/SignUp.png" },
      { bold: "Secure Authentication", desc: "Enterprise-grade security protecting your work.", image: "/images/video/SignIn.png" }
    ],
    image: "/images/presentation/placeholder_signup.png",
    duration: 10000
  },
  {
    id: "create_project",
    title: "Start Your First Project",
    features: [
      { bold: "One-Click Creation", desc: "Create a new LaTeX project from your dashboard instantly.", image: "/images/video/CreateProject.png" },
      { bold: "Apply Custom Templates", desc: "Link your project to existing styles and layouts.", image: "/images/video/ApplyTemplate.png" },
      { bold: "Organized Dashboard", desc: "Manage all your publications and files in one place.", image: "/images/video/Dashboard.png" }
    ],
    image: "/images/presentation/placeholder_create.png",
    duration: 11000
  },
  {
    id: "upload_file",
    title: "Seamless File Uploads",
    features: [
      { bold: "Drag & Drop Support", desc: "Easily upload multiple files directly into the workspace.", image: "/images/video/UploadFiles.png" },
      { bold: "Broad Compatibility", desc: "Supports .tex, .bib, .cls, .sty, .txt, and image formats (.jpg, .png, .eps).", image: "/images/video/MoreFileFormates.png" },
      { bold: "Automatic File Indexing", desc: "Mark your main file and we handle the rest.", image: "/images/video/AutoFileIndex.png" }
    ],
    image: "/images/presentation/placeholder_upload.png",
    duration: 11000
  },
  {
    id: "workspace",
    title: "The Editor's Workspace",
    features: [
      { bold: "All-In-One View", desc: "See the LaTeX code, live PDF preview, and queries side-by-side.", image: "/images/video/AllInOneView.png" },
      { bold: "Powerful Search & Replace", desc: "Use advanced Regex to fix styling issues across the document.", image: "/images/video/SearchAndReplace.png" },
      { bold: "Clear Error Messages", desc: "Points you directly to the exact line of code needing fixes.", image: "/images/video/ErrorShow.png" }
    ],
    image: "/images/presentation/three_pane.png",
    duration: 11000
  },
  {
    id: "environment",
    title: "Templates & Environments",
    features: [
      { bold: "Reusable Assets", desc: "Create your own 'Environments' to store common bibliographies and styles.", image: "/images/video/CreateOwnEnvironment.png" },
      { bold: "Consistent Branding", desc: "Apply the same environment across multiple projects for uniformity.", image: "/images/video/ApplyTemplate.png" },
      { bold: "Browse Templates", desc: "Select from pre-configured journal and book templates.", image: "/images/video/BrowseTemplate.png" }
    ],
    image: "/images/presentation/placeholder_environment.png",
    duration: 11000
  },
  {
    id: "shortcuts",
    title: "Powerful Macros & Shortcuts",
    features: [
      { bold: "Smart Auto-Casing", desc: "Use Alt+Shift+x to instantly format sentences to standard title case.", image: "/images/video/TitleCase.png" },
      { bold: "Quick Creation", desc: "Press Ctrl+F2 to instantly spawn a new LaTeX file.", image: "/images/video/QuickCreation.png" },
      { bold: "Instant Snippets", desc: "Built-in macros for \\textbf, \\textit, \\cmidrule, and more.", image: "/images/video/InstantSnippets.png" }
    ],
    image: "/images/video/placeholder_shortcuts.png",
    duration: 11000
  },
  {
    id: "compile",
    title: "Smart Compilation",
    features: [
      { bold: "Dynamic Compilers", desc: "Automatically switches between PDFLaTeX, XeLaTeX, and LuaLaTeX.", image: "/images/video/Compiler.png" },
      { bold: "Auto-Compile Engine", desc: "Compiles your document seamlessly in isolated Docker sandboxes.", image: "/images/presentation/math_2.png" },
      { bold: "Instant Math Previews", desc: "Preview complex equations visually before running a full build.", image: "/images/video/InstantMathPreview.png" }
    ],
    image: "/images/presentation/math.png",
    duration: 11000
  },
  {
    id: "collab",
    title: "Real-Time Collaboration",
    features: [
      { bold: "True Teamwork", desc: "Authors and multiple editors can type concurrently on the same file.", image: "/images/video/TeamWork.png" },
      { bold: "Live Cursors", desc: "See colored cursors and names jumping around the screen.", image: "/images/presentation/collab_2.png" },
      { bold: "Safe Merging", desc: "High-speed caching ensures no work is ever overwritten or lost.", image: "/images/presentation/collab_3.png" }
    ],
    image: "/images/presentation/collab.png",
    duration: 11000
  },
  {
    id: "publisher",
    title: "Built for Publishers",
    features: [
      { bold: "One-Click Downloads", desc: "Get the final PDF, LaTeX source, and logs with one button.", image: "/images/video/Download.png" },
      { bold: "Automatic XML Generation", desc: "Builds perfect digital XML text for websites and eReaders.", image: "/images/video/XMLGeneration.png" },
      { bold: "Auto-Pagination", desc: "Builds perfect PDF pages automatically without manual help.", image: "/images/video/AutoPagination.png" }
    ],
    image: "/images/presentation/download.png",
    duration: 11000
  },
  {
    id: "proofing",
    title: "Author Proofing & Comments",
    features: [
      { bold: "Direct Comments", desc: "Leave sticky notes and questions attached directly to a sentence.", image: "/images/presentation/author_1.png" },
      { bold: "Instant Feedback", desc: "When authors answer a question, the change is verified instantly.", image: "/images/presentation/author_2.png" },
      { bold: "Status Dashboard", desc: "Track exactly how many questions are left open before printing.", image: "/images/presentation/author_3.png" }
    ],
    image: "/images/presentation/author.png",
    duration: 11000
  },
  {
    id: "sync",
    title: "Click the PDF, Find the Code",
    features: [
      { bold: "SyncTeX Integration", desc: "Mathematically links the text on the PDF back to the raw code.", image: "/images/presentation/toc_1.png" },
      { bold: "Fix Typos Instantly", desc: "Click a word on the PDF, and your cursor jumps to that line!", image: "/images/video/FixTypoInstantly.png" },
      { bold: "No More Hunting", desc: "Never manually scroll through thousands of lines of code again.", image: "/images/presentation/toc_3.png" }
    ],
    image: "/images/presentation/toc.png",
    duration: 11000
  },
  {
    id: "pipeline",
    title: "The EditLaTeX Automatic Pipeline",
    features: [
      { bold: "1. Check & Structure", desc: "Automatically check files and safely organize messy templates." },
      { bold: "2. Clean & Merge", desc: "Force standard formatting and organize bibliography citations." },
      { bold: "3. Build & Deliver", desc: "Generate XML, auto-paginate the PDF, and finish projects perfectly." }
    ],
    isPipeline: true,
    duration: 12000
  },
  {
    id: "outro",
    title: "Thank You",
    text: "Ready to experience the future of digital typesetting? Let's build something incredible together.",
    image: "/images/white-logo.png",
    isHero: true,
    duration: 10000
  }
];

const TotalDuration = demoSteps.reduce((acc, step) => acc + step.duration, 0);

const ProductDemoVideo = () => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let timer;
    if (isPlaying) {
      timer = setInterval(() => {
        setProgress((prev) => {
          if (prev >= TotalDuration) {
            clearInterval(timer);
            return TotalDuration;
          }
          return prev + 50;
        });
      }, 50);
    }
    return () => clearInterval(timer);
  }, [isPlaying]);

  useEffect(() => {
    if (progress === TotalDuration) {
      setIsPlaying(false);
    }

    let accumulatedTime = 0;
    for (let i = 0; i < demoSteps.length; i++) {
      accumulatedTime += demoSteps[i].duration;
      if (progress < accumulatedTime) {
        if (currentStepIndex !== i) {
          setCurrentStepIndex(i);
        }
        break;
      }
    }
  }, [progress, currentStepIndex]);

  const togglePlay = () => {
    if (progress >= TotalDuration) {
      setProgress(0);
      setCurrentStepIndex(0);
      setIsPlaying(true);
    } else {
      setIsPlaying(!isPlaying);
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    setProgress(percentage * TotalDuration);
  };

  const currentStep = demoSteps[currentStepIndex];
  const timeInStep = progress - demoSteps.slice(0, currentStepIndex).reduce((acc, s) => acc + s.duration, 0);

  return (
    <Box w="100vw" h="100vh" bg="var(--bg)" overflow="hidden" position="relative" fontFamily="inherit" color="var(--text-primary)">
      {/* Cinematic Ambient Background */}
      <Box position="absolute" top="-30%" left="-20%" w="70%" h="70%" bg="var(--primary)" filter="blur(250px)" opacity={0.15} borderRadius="full" />
      <Box position="absolute" bottom="-30%" right="-20%" w="70%" h="70%" bg="var(--btn-bg)" filter="blur(250px)" opacity={0.15} borderRadius="full" />

      {/* Main Video Content Area */}
      <Flex w="100%" h="100%" alignItems="center" justifyContent="center" position="relative" zIndex={10} pb={{ base: "80px", md: "100px" }}>

        <Box w="100%" h="100%" maxW="1920px" position="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep.id}
              initial={{ opacity: 0, filter: "blur(10px)", scale: 1.02 }}
              animate={{ opacity: 1, filter: "blur(0px)", scale: 1 }}
              exit={{ opacity: 0, filter: "blur(10px)", scale: 0.98 }}
              transition={{ duration: 1.2, ease: "easeInOut" }}
              style={{ width: "100%", height: "100%", position: "absolute", top: 0, left: 0 }}
            >
              {currentStep.isHero ? (
                <Flex direction="column" alignItems="center" justifyContent="center" h="100%" textAlign="center" px={4}>
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, ease: "easeOut" }}
                  >
                    {currentStep.image && (
                      <Image src={process.env.PUBLIC_URL + currentStep.image} alt="Logo" maxH="150px" mb={10} />
                    )}
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={timeInStep > 1200 ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                  >
                    <Text fontSize={{ base: "4xl", md: "7xl" }} fontWeight="800" bgGradient="var(--gradient-primary)" bgClip="text" mb={8} letterSpacing="tight">
                      {currentStep.title}
                    </Text>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={timeInStep > 2600 ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                  >
                    <Text fontSize={{ base: "xl", md: "3xl" }} color="var(--text-secondary)" maxW="900px" lineHeight="1.6" fontWeight="medium">
                      {currentStep.text}
                    </Text>
                  </motion.div>
                </Flex>
              ) : currentStep.isPipeline ? (
                <Flex direction="column" alignItems="center" justifyContent="center" h="100%" px={8}>
                  <Text fontSize={{ base: "3xl", md: "5xl" }} fontWeight="800" mb={16} color="var(--text-primary)">{currentStep.title}</Text>
                  <Flex w="100%" maxW="1400px" justifyContent="space-between" alignItems="stretch" position="relative" direction={{ base: "column", lg: "row" }} gap={8}>
                    <Box display={{ base: "none", lg: "block" }} position="absolute" top="50%" left="5%" right="5%" h="3px" bgGradient="var(--gradient-primary)" zIndex={0} opacity={0.6} />

                    {currentStep.features.map((feature, idx) => {
                      const isVisible = timeInStep > (1000 + idx * 2500);
                      return (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, y: 30 }}
                          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                          transition={{ duration: 0.8, ease: "easeOut" }}
                          style={{ flex: 1, display: "flex", width: "100%", zIndex: 1 }}
                        >
                          <VStack
                            bg="var(--card-bg)"
                            backdropFilter="blur(16px)"
                            p={8}
                            borderRadius="2xl"
                            border="1px solid"
                            borderColor="var(--border)"
                            w="100%"
                            boxShadow="var(--shadow-lg)"
                          >
                            <Flex w="80px" h="80px" borderRadius="full" bgGradient="var(--gradient-primary)" alignItems="center" justifyContent="center" mb={6} boxShadow="var(--shadow-glow)">
                              <Icon as={FaChevronRight} color="var(--btn-text)" boxSize={6} />
                            </Flex>
                            <Text fontSize="2xl" fontWeight="bold" textAlign="center" mb={4} color="var(--text-primary)">{feature.bold}</Text>
                            <Text color="var(--text-secondary)" textAlign="center" fontSize="md" lineHeight="1.7">{feature.desc}</Text>
                          </VStack>
                        </motion.div>
                      );
                    })}
                  </Flex>
                </Flex>
              ) : (
                <Flex h="100%" w="100%" direction={{ base: "column", lg: "row" }} alignItems="center" justifyContent="space-between" px={{ base: 6, md: 10, lg: 12 }} py={6} gap={{ base: 8, lg: 10 }}>
                  {/* Left Column: Content Text & Features */}
                  <Box w={{ base: "100%", lg: "35%" }} maxW="480px" zIndex={2}>
                    <Text fontSize={{ base: "4xl", md: "5xl" }} fontWeight="800" mb={8} bgGradient="var(--gradient-primary)" bgClip="text" letterSpacing="tight" lineHeight="1.2">
                      {currentStep.title}
                    </Text>
                    <VStack align="flex-start" spacing={8}>
                      {currentStep.features.map((feature, idx) => {
                        const isVisible = timeInStep > (800 + idx * 2500);

                        return (
                          <motion.div
                            key={idx}
                            initial={{ opacity: 0, x: -30 }}
                            animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                            style={{ width: "100%" }}
                          >
                            <Flex align="flex-start">
                              <Box mt={1.5} mr={4} p={1.5} bg="var(--btn-bg)" borderRadius="full">
                                <Icon as={FaChevronRight} color="var(--btn-text)" boxSize={3} display="block" />
                              </Box>
                              <Box>
                                <Text fontSize="xl" fontWeight="bold" mb={2} color="var(--text-primary)">{feature.bold}</Text>
                                <Text fontSize="lg" color="var(--text-secondary)" lineHeight="1.6">{feature.desc}</Text>
                              </Box>
                            </Flex>
                          </motion.div>
                        );
                      })}
                    </VStack>
                  </Box>

                  {/* Right Column: Image Preview in a gorgeous card */}
                  <Box
                    w={{ base: "100%", lg: "60%" }}
                    h={{ base: "320px", md: "480px", lg: "70vh" }}
                    position="relative"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    zIndex={1}
                    borderRadius="2xl"
                    border="1px solid"
                    borderColor="var(--border)"
                    overflow="hidden"
                    boxShadow="var(--shadow-lg)"
                    bg="var(--card-bg)"
                    p={2}
                  >
                    <AnimatePresence mode="wait">
                      {(() => {
                        const visibleCount = currentStep.features.filter((_, idx) => timeInStep > (800 + idx * 2500)).length;
                        const activeIdx = Math.max(0, visibleCount - 1);

                        let baseImage = currentStep.image;
                        let activeImage = baseImage;

                        // Use the specific image for the bullet point if defined
                        if (currentStep.features[activeIdx] && currentStep.features[activeIdx].image) {
                          activeImage = currentStep.features[activeIdx].image;
                        }

                        return (
                          <motion.img
                            key={activeImage}
                            src={process.env.PUBLIC_URL + activeImage}
                            alt={currentStep.title}
                            style={{
                              position: "absolute",
                              objectFit: "contain",
                              width: "calc(100% - 16px)",
                              height: "calc(100% - 16px)",
                              maxWidth: "100%",
                              maxHeight: "100%",
                            }}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ duration: 0.8 }}
                            onError={(e) => {
                              // Fallback if the specific _1.png doesn't exist, show the base image or generic placeholder
                              e.target.onerror = null;
                              e.target.src = "https://via.placeholder.com/1920x1080/1a202c/ffffff?text=Image+Placeholder+(Add+in+Future)";
                            }}
                          />
                        );
                      })()}
                    </AnimatePresence>
                  </Box>
                </Flex>
              )}
            </motion.div>
          </AnimatePresence>
        </Box>
      </Flex>

      {/* Modern Floating Video Controls Footer */}
      <Box position="absolute" bottom={0} left={0} w="100%" px={6} py={6} zIndex={30} style={{ background: 'linear-gradient(to top, var(--bg) 0%, transparent 100%)' }}>
        <Flex maxW="1600px" mx="auto" alignItems="center" gap={6} bg="var(--card-bg)" backdropFilter="blur(20px)" p={4} borderRadius="2xl" border="1px solid" borderColor="var(--border)" boxShadow="var(--shadow-md)">
          <Box cursor="pointer" onClick={togglePlay} color="var(--text-primary)" _hover={{ color: "var(--primary)", transform: "scale(1.1)" }} transition="all 0.2s" display="flex" alignItems="center" justifyContent="center" w={10} h={10}>
            {progress >= TotalDuration ? <FaRedo size={22} /> : isPlaying ? <FaPause size={22} /> : <FaPlay size={22} />}
          </Box>

          <Box flex={1} position="relative" h="10px" bg="var(--border)" borderRadius="full" cursor="pointer" onClick={handleSeek} overflow="hidden">
            <Box position="absolute" top={0} left={0} h="100%" bg="var(--primary)" borderRadius="full" width={`${(progress / TotalDuration) * 100}%`} transition="width 0.05s linear" />

            {demoSteps.map((step, idx) => {
              if (idx === 0) return null;
              const prevDuration = demoSteps.slice(0, idx).reduce((acc, s) => acc + s.duration, 0);
              const leftPercent = (prevDuration / TotalDuration) * 100;
              return (
                <Box
                  key={idx}
                  position="absolute"
                  top={0}
                  left={`${leftPercent}%`}
                  w="2px"
                  h="100%"
                  bg="var(--text-secondary)"
                  opacity={0.3}
                  transform="translateX(-50%)"
                />
              );
            })}
          </Box>

          <Text fontSize="md" color="var(--text-primary)" fontWeight="600" w="130px" textAlign="right" fontFamily="monospace" letterSpacing="wide">
            {Math.floor(progress / 1000 / 60)}:{(Math.floor(progress / 1000) % 60).toString().padStart(2, '0')} / {Math.floor(TotalDuration / 1000 / 60)}:{(Math.floor(TotalDuration / 1000) % 60).toString().padStart(2, '0')}
          </Text>
        </Flex>
      </Box>
    </Box>
  );
};

export default ProductDemoVideo;
